import { useState } from "react";
import type { DatumId } from "@nivo/pie";
import { t } from "@/i18n-js/instance";
import { classNames } from "@/react/helpers/twMergeWithCN";
import { Button } from "@circle-react-uikit/Button";
import { Typography } from "@circle-react-uikit/Typography";

interface LegendComponentProps {
  className?: string;
  data: {
    id: DatumId;
    color: string;
    label: string;
  }[];
  orientation?: "horizontal" | "vertical";
  rootLocale?: string[];
  setActiveId: (id: DatumId | null) => void;
}

const MAX_ITEMS = 10;

export const LegendComponent = ({
  data,
  orientation = "horizontal",
  className,
  setActiveId,
  rootLocale,
}: LegendComponentProps) => {
  const [visibleItems, setVisibleItems] = useState(MAX_ITEMS);

  if (!data || data.length === 0) {
    return null;
  }

  const itemsToShow = data.slice(0, visibleItems);
  const remainingCount = data.length - visibleItems;
  const hasMoreItems = remainingCount > 0;

  const handleShowMore = () => {
    setVisibleItems(prev => Math.min(prev + MAX_ITEMS, data.length));
  };

  return (
    <div
      className={classNames(
        "flex gap-4",
        orientation === "horizontal"
          ? "flex-row flex-wrap justify-center"
          : "flex-col",
        className,
      )}
    >
      {itemsToShow.map(item => (
        <Button
          key={item.label}
          variant="text"
          small
          className="flex h-auto cursor-pointer items-center gap-2 p-0"
          onMouseOver={() => setActiveId(item.id)}
          onFocus={() => setActiveId(item.id)}
          onMouseLeave={() => setActiveId(null)}
          onBlur={() => setActiveId(null)}
        >
          <div
            className="size-3 shrink-0 rounded-full"
            style={{ backgroundColor: item.color }}
          />
          <Typography.BodySm color="text-secondary dark:text-white">
            {rootLocale ? t([...rootLocale, item.label]) : item.label}
          </Typography.BodySm>
        </Button>
      ))}

      {hasMoreItems && (
        <Button
          variant="plain"
          small
          onClick={handleShowMore}
          className="flex h-auto cursor-pointer items-center gap-2 p-0 text-sm font-medium"
        >
          {remainingCount === 1
            ? t("analytics.legend.show_more_singular", {
                count: remainingCount,
              })
            : t("analytics.legend.show_more", { count: remainingCount })}
        </Button>
      )}
    </div>
  );
};
